import { create } from 'zustand';
import api from '../utils/api';

// Auth is a server-side session: the browser holds an HttpOnly `dep_session`
// cookie that JS cannot read, so nothing credential-like is stored here.
const useAuthStore = create((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  authDisabled: false,
  lastAuthCheck: 0,

  // Check if user is authenticated
  checkAuth: async (force = false) => {
    // Only check auth once per 5 seconds unless forced
    const now = Date.now();
    const { lastAuthCheck } = get();

    if (!force && now - lastAuthCheck < 5000) {
      return get().isAuthenticated;
    }

    // Check if the server has auth disabled
    try {
      const statusResponse = await api.get('/auth/status');
      if (statusResponse.data?.auth_disabled) {
        set({ authDisabled: true });
        let user;
        try {
          user = (await api.get('/auth/me')).data;
        } catch {
          // Server says auth disabled but /auth/me failed -- still let them in
          user = { id: 0, username: 'anonymous', email: '', is_superuser: true };
        }
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          authDisabled: true,
          lastAuthCheck: now,
        });
        return true;
      }
    } catch {
      // Could not reach auth/status -- fall through to normal auth flow
    }

    set({ authDisabled: false, isLoading: true });

    try {
      const response = await api.get('/auth/me');
      set({
        user: response.data,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        lastAuthCheck: now,
      });
      return true;
    } catch (error) {
      // 401 simply means "not logged in"; anything else is worth surfacing
      const unauthenticated = error.response?.status === 401;
      if (!unauthenticated) console.error('Auth check failed:', error);
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: unauthenticated ? null : (error.response?.data?.detail || 'Authentication error'),
        lastAuthCheck: now,
      });
      return false;
    }
  },

  // Login user (server sets the HttpOnly session cookie)
  login: async (username, password) => {
    set({ isLoading: true, error: null });

    try {
      const response = await api.post('/auth/login', { username, password });
      set({
        user: response.data,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        lastAuthCheck: Date.now(),
      });
      return true;
    } catch (error) {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: error.response?.data?.detail || 'Login failed. Please check your credentials.',
      });
      return false;
    }
  },

  // Logout user (server clears the cookie)
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout request failed:', error);
    }
    set({
      user: null,
      isAuthenticated: false,
      error: null,
      lastAuthCheck: 0,
    });
    return true;
  },

  // Clear error
  clearError: () => set({ error: null }),
}));

// Hook for components to use
export const useAuth = useAuthStore;

export default useAuthStore;
